import React, { useState, useEffect, useCallback } from "react";
import { api } from "./api";

const fmtDate = (d) => (d ? new Date(d).toLocaleDateString("en-NP") : "—");

export default function Dashboard({ onOpen, onNew }) {
  const [reports, setReports] = useState([]);
  const [total, setTotal]     = useState(0);
  const [search, setSearch]   = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState("");
  const [delId, setDelId]     = useState(null);

  const load = useCallback(async (q = search) => {
    setLoading(true);
    setError("");
    try {
      const data = await api.listReports({ search: q, limit: 100 });
      setReports(data.reports);
      setTotal(data.total);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [search]);

  useEffect(() => { load(); }, []);

  const handleDelete = async (id) => {
    try {
      await api.deleteReport(id);
      setDelId(null);
      load();
    } catch (e) {
      alert("Delete failed: " + e.message);
    }
  };

  return (
    <div style={styles.wrap}>
      {/* Header */}
      <div style={styles.header}>
        <div>
          <h1 style={styles.title}>📋 Valuation Reports</h1>
          <p style={styles.sub}>{total} report{total !== 1 ? "s" : ""} saved in database</p>
        </div>
        <button style={styles.btnPrimary} onClick={onNew}>
          ＋ New Report
        </button>
      </div>

      {/* Search */}
      <div style={styles.searchRow}>
        <input
          style={styles.search}
          placeholder="Search by client, bank, or branch…"
          value={search}
          onChange={e => setSearch(e.target.value)}
          onKeyDown={e => e.key === "Enter" && load(search)}
        />
        <button style={styles.btnSearch} onClick={() => load(search)}>Search</button>
        {search && (
          <button style={styles.btnClear} onClick={() => { setSearch(""); load(""); }}>✕ Clear</button>
        )}
      </div>

      {/* Error */}
      {error && (
        <div style={styles.errorBox}>
          ⚠ Cannot reach backend: {error}. Make sure the backend is running on port 3001.
        </div>
      )}

      {/* Table */}
      {loading ? (
        <div style={styles.center}>Loading…</div>
      ) : reports.length === 0 ? (
        <div style={styles.empty}>
          <p>No reports found.</p>
          <button style={styles.btnPrimary} onClick={onNew}>Create your first report →</button>
        </div>
      ) : (
        <div style={styles.tableWrap}>
          <table style={styles.table}>
            <thead>
              <tr>
                {["#","Client / Owner","Bank","Branch","Type","Visit Date","Report Date","Saved",""].map(h => (
                  <th key={h} style={styles.th}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {reports.map((r, i) => (
                <tr key={r.id} style={i % 2 === 0 ? styles.trEven : styles.trOdd}>
                  <td style={styles.td}>{r.id}</td>
                  <td style={{ ...styles.td, fontWeight: 500 }}>{r.client_name || "—"}</td>
                  <td style={styles.td}>{r.bank || "—"}</td>
                  <td style={styles.td}>{r.branch || "—"}</td>
                  <td style={styles.td}>
                    <span style={{
                      ...styles.badge,
                      background: r.report_type === "preliminary" ? "#e8f4fd" : "#e8fdf0",
                      color: r.report_type === "preliminary" ? "#1565c0" : "#2e7d32",
                    }}>
                      {r.report_type}
                    </span>
                  </td>
                  <td style={styles.td}>{fmtDate(r.visit_date)}</td>
                  <td style={styles.td}>{fmtDate(r.report_date)}</td>
                  <td style={styles.tdSm}>{fmtDate(r.updated_at)}</td>
                  <td style={styles.tdActions}>
                    <button style={styles.btnOpen} onClick={() => onOpen(r.id)}>Open</button>
                    <button
                      style={styles.btnDel}
                      onClick={() => setDelId(r.id)}
                    >Delete</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Delete confirm modal */}
      {delId && (
        <div style={styles.overlay}>
          <div style={styles.modal}>
            <h3 style={{ marginBottom: 12 }}>Delete report #{delId}?</h3>
            <p style={{ color: "#555", marginBottom: 20 }}>This cannot be undone.</p>
            <div style={{ display: "flex", gap: 10, justifyContent: "flex-end" }}>
              <button style={styles.btnCancel} onClick={() => setDelId(null)}>Cancel</button>
              <button style={styles.btnDelConfirm} onClick={() => handleDelete(delId)}>Delete</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

const styles = {
  wrap: { maxWidth: 1200, margin: "0 auto", padding: "32px 24px", fontFamily: "'DM Sans', sans-serif" },
  header: { display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 28, flexWrap: "wrap", gap: 12 },
  title: { fontSize: 26, fontWeight: 700, color: "#0f1f3d", margin: 0 },
  sub: { color: "#666", marginTop: 4, fontSize: 14 },
  btnPrimary: { background: "#c9922a", color: "#fff", border: "none", borderRadius: 8, padding: "10px 20px", fontWeight: 600, cursor: "pointer", fontSize: 15 },
  searchRow: { display: "flex", gap: 8, marginBottom: 24, flexWrap: "wrap" },
  search: { flex: 1, minWidth: 200, padding: "10px 14px", border: "1.5px solid #ddd", borderRadius: 8, fontSize: 14 },
  btnSearch: { padding: "10px 18px", background: "#0f1f3d", color: "#fff", border: "none", borderRadius: 8, cursor: "pointer", fontWeight: 600 },
  btnClear: { padding: "10px 14px", background: "#f1f1f1", border: "none", borderRadius: 8, cursor: "pointer" },
  errorBox: { background: "#fff3cd", border: "1px solid #ffc107", borderRadius: 8, padding: "14px 18px", marginBottom: 20, color: "#7a5000" },
  center: { textAlign: "center", padding: 40, color: "#888" },
  empty: { textAlign: "center", padding: "60px 20px", color: "#555" },
  tableWrap: { overflowX: "auto", borderRadius: 10, border: "1px solid #e5e7eb" },
  table: { width: "100%", borderCollapse: "collapse", fontSize: 14 },
  th: { background: "#0f1f3d", color: "#fff", padding: "12px 14px", textAlign: "left", fontWeight: 600, whiteSpace: "nowrap" },
  td: { padding: "11px 14px", color: "#333", borderBottom: "1px solid #f0f0f0" },
  tdSm: { padding: "11px 14px", color: "#888", fontSize: 12, whiteSpace: "nowrap", borderBottom: "1px solid #f0f0f0" },
  tdActions: { padding: "8px 14px", whiteSpace: "nowrap", borderBottom: "1px solid #f0f0f0" },
  trEven: { background: "#fff" },
  trOdd: { background: "#fafafa" },
  badge: { padding: "3px 9px", borderRadius: 20, fontSize: 12, fontWeight: 600 },
  btnOpen: { background: "#0f1f3d", color: "#fff", border: "none", borderRadius: 6, padding: "5px 12px", cursor: "pointer", marginRight: 6, fontSize: 13 },
  btnDel: { background: "#fff", color: "#c0392b", border: "1px solid #c0392b", borderRadius: 6, padding: "5px 12px", cursor: "pointer", fontSize: 13 },
  overlay: { position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 9999 },
  modal: { background: "#fff", borderRadius: 12, padding: 28, minWidth: 300, boxShadow: "0 20px 60px rgba(0,0,0,0.2)" },
  btnCancel: { padding: "8px 18px", border: "1px solid #ccc", borderRadius: 6, background: "#fff", cursor: "pointer" },
  btnDelConfirm: { padding: "8px 18px", background: "#c0392b", color: "#fff", border: "none", borderRadius: 6, cursor: "pointer", fontWeight: 600 },
};
